<script lang="ts">
  import type { Order, ProductOption } from "../../../@types/api";
  import { OrderHistory, Popup, Login } from "../../../shared/bloc";
  import { Button, Flags, Modal } from "../../../shared/components";
  import { deleteCookie, getCookie } from "../../../shared/utils";
  import {
    COOKIES_NAME,
    createOrder,
    getOptions,
    getOrder,
    getOrders,
    loginGoogle,
    loginFacebook,
    proceedPayment,
    updateOrder, APP_COOKIES,
  } from "./api";

  const params = new URLSearchParams(location.search);
  const orderId = params.get("orderId");
  const isHistoryView = params.get("history");
  const prefix = "+60";


  let order: Order | null = null;
  let error: string | null = null;

  enum stateName {
    PROVIDER = "provider",
    AMOUNT = "amount",
  }

  let msisdn = "";
  let loading = false;
  let lostfocus = false;
  let products: ProductOption[] = [];
  let showModal = false;
  let state = {
    provider: -1,
    amount: 0,
  };
  let showApplyVoucherModal = false;
  let discountAmount = 0;
  let voucherCodeInput = "";
  let voucherCodeError = "";

  const orderMap = {
    "TVk6TUFYSVM=": 0,
    "TVk6Q0VMQ09N": 1,
    "TVk6RElHSQ==": 2,
    "TVk6VU1PQklMRQ==": 3,
    "TVk6UkVET05F": 4,
    "TVk6WE9Y": 5,
    "TVk6WUVT": 6,
    "TVk6VFVORVRBTEs=": 7,
    "TVk6QUxURUw=": 8,
    "TVk6TUVSQ0hBTlRSQURF": 9,
  }

  $: if (msisdn.length > 10) {
    msisdn = msisdn.substr(0, 10);
  }
  $: validMsisdn = /^0?1[02-9][0-9]{7}$|^0?11[0-9]{7,8}$/g.test(msisdn);
  $: hasError = msisdn.length > 0 && lostfocus && !validMsisdn;
  $: payableAmount = ((products[state.provider]?.options[state.amount].amount || 0) - discountAmount).toFixed(2);
  $: ongoingOrderId = getCookie("order_id");
  $: isLoginPage = window.location.hash.includes("#/login")

  const getAttribute = (e: Event, attr: string): string | null => {
    const { currentTarget = document.body } = e;
    let { target }: any = e;
    while (target != currentTarget) {
      if (target && target.hasAttribute(attr)) return target.getAttribute(attr);
      target = target.parentNode;
    }
    if (target && target.hasAttribute(attr)) return target.getAttribute(attr);
    return null;
  };

  getOptions().then((items) => {
    products = items.map((v) => ({
      ...v,
      options: [...new Set(v.options.map(JSON.stringify))].map((v) => JSON.parse(v)),
      order: orderMap[v.id]
    })).sort((a, b) => {
      if (a.order === undefined) return 1;
      if (a.order > b.order) return 1;
      if (a.order < b.order) return -1;
    });
  });

  const onSelect = (name: stateName) => (e: Event) => {
    const value = getAttribute(e, "data-index");
    if (!value) return;
    state[name] = parseInt(value, 10);
    //
    const orderId = getCookie("prepaid.order.id")
    if (orderId) {
      loading = true
      updateOrder(
        orderId,
        `${prefix}${msisdn}`,
        products[state.provider].options[state.amount].id,
        voucherCodeInput
      ).then(({ voucher, order }) => {
        if (voucher && voucher.errorMessage) {
          voucherCodeError = voucher.errorMessage
        } else if (voucher && voucher.id && order.discountAmount) {
          discountAmount = order.discountAmount
        }
      })
        .catch((err) => {
        // show error
        showModal = false;
        showApplyVoucherModal = false;
        if (err.debug) {
          error = err.debug;
        } else {
          error = err.message;
        }
      }).finally(() => {
        loading = false
      })
    }
  };

  const APP_NAME = decodeURIComponent(getCookie(APP_COOKIES))

  const pay = () => {
    const orderId = getCookie("prepaid.order.id")
    if (orderId) {
      loading = true
      proceedPayment(orderId).then(({ paymentUrl }: { paymentUrl: string }) => {
        deleteCookie("prepaid.order.id", true)
        window.location.href = paymentUrl;
      }).finally(() => {
        loading = false;
      })
    }
  }

  const onConfirm = () => {
    loading = true;
    error = null;
    createOrder(
      `${prefix}${msisdn}`,
      products[state.provider].options[state.amount].id,
    )
      .then(({ order, voucher }) => {
        showApplyVoucherModal = true
        if (voucher !== null) {
          voucherCodeInput = voucher.code;
          discountAmount = order.discountAmount;
        }
      })
      .catch((err) => {
        // show error
        showModal = false;
        loading = false;
        if (err.debug) {
          error = err.debug;
        } else {
          error = err.message;
        }
      }).finally(() => loading = false);
  };

  const cancelVoucher = () => {
    const orderId = getCookie("prepaid.order.id")
    if (orderId) {
      loading = true
      updateOrder(
        orderId,
        `${prefix}${msisdn}`,
        products[state.provider].options[state.amount].id,
      ).then(() => {
        voucherCodeInput = ""
        discountAmount = 0
      }).finally(() => {
        loading = false
      })
    }
  }

  const onApplyVoucher = () => {
    voucherCodeError = ""
    loading = true
    const orderId = getCookie("prepaid.order.id")
    let pFunc: Promise<unknown>;
    if (!orderId) {
      pFunc = createOrder(
        `${prefix}${msisdn}`,
        products[state.provider].options[state.amount].id,
      )
    } else {
      pFunc = updateOrder(
        orderId,
        `${prefix}${msisdn}`,
        products[state.provider].options[state.amount].id,
        voucherCodeInput,
      )
    }
    pFunc.then(({ voucher, order }) => {
      if (voucher && voucher.errorMessage) {
        voucherCodeError = voucher.errorMessage
      } else if (voucher && voucher.id && order.discountAmount) {
        discountAmount = order.discountAmount
      }
    }).catch((err) => {
      // show error
      showModal = false;
      showApplyVoucherModal = false;
      if (err.debug) {
        error = err.debug;
      } else {
        error = err.message;
      }
    }).finally(() => {
        loading = false;
      });
  };

  const viewHistories = () => {
    params.set("history", "true");
    window.location.search = params.toString();
  };

  const login = (type: "google" | "facebook" = "google") => {
    let func: () => Promise<{url: string}>
    if (type === 'google') {
      func = loginGoogle
    } else if (type === 'facebook') {
      func = loginFacebook
    }
    if (func) {
      loading = true;
      func().then((item) => {
        window.location.href = item.url
      }).finally(() => {
        loading = false;
      })
    }
  };

  const sEcoCashbackImageMap = {
    "2.5": "https://topup-asset.oss-ap-southeast-3.aliyuncs.com/images/seco/S-Coin-New-Label_Senheng-App2_01.png",
    "3": "https://topup-asset.oss-ap-southeast-3.aliyuncs.com/images/seco/S-Coin-New-Label_Senheng-App2_02.png",
    "5": "https://topup-asset.oss-ap-southeast-3.aliyuncs.com/images/seco/S-Coin-New-Label_Senheng-App2_03.png",
    "6": "https://topup-asset.oss-ap-southeast-3.aliyuncs.com/images/seco/S-Coin-New-Label_Senheng-App2_04.png",
  }
</script>

<svelte:head>
  <link href="https://fonts.googleapis.com" rel="preconnect"/>
  <link crossorigin="true" href="https://fonts.gstatic.com" rel="preconnect"/>
  <link
    href="https://fonts.googleapis.com/css2?family=Inter:wght@400;600&display=swap"
    rel="stylesheet"
  />
</svelte:head>

{#if isLoginPage}
  <Login loginFunc="{login}" />
{:else if orderId}
  <Popup category="TELECOMMUNICATION" {getOrder}/>
{:else if isHistoryView}
  <OrderHistory category="TELECOMMUNICATION" {getOrders}/>
{:else}
  <div class="padding">
    <div class="header">
      <h1 style="flex:1">Top up mobile prepaid</h1>
      <div class="icon" on:click={() => viewHistories()}>
        <svg
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      </div>
    </div>
    <div style="padding-bottom:20px">
      {#if APP_NAME === "S ECOSYSTEM"}
        Top up your mobile credit and earn S-Coin cashback without hassle
      {:else}
        Top up your mobile credit without hassle.
        {/if}
    </div>
    <div class="subtitle">Phone Number</div>
    <div class="input__wrapper">
      <Flags countryCode="my"/>
      <span class="input__prefix">
        ({prefix})
      </span>
      <input
        inputmode="numeric"
        bind:value={msisdn}
        on:blur={() => (lostfocus = true)}
        on:focus={() => (lostfocus = false)}
        autofocus
        autocomplete="off"
        class:error={hasError}
        type="text"
      />
      <span class="input__underline" class:error={hasError}/>
    </div>
    {#if hasError}
      <div class="error">Invalid phone number</div>
    {:else if error}
      <div class="error">{error}</div>
    {/if}
    <div style="padding-top:1rem;">Select Provider</div>
    <ul class="telco-options" on:click={onSelect(stateName.PROVIDER)}>
      {#each products as item, i (item.id)}
        <li data-index={i} class:selected={state.provider === i}>
          <div class="option">
            {#if item.metadata && item.metadata[APP_NAME]?.CASHBACK}
              <img class="cashback-image" alt={item.metadata[APP_NAME].CASHBACK} src={`https://topup-asset.oss-ap-southeast-3.aliyuncs.com/images/seco/${item.metadata[APP_NAME].CASHBACK}%25.png`} />
              {/if}
            <img class="product-image" src={item.imageUrl} alt={item.id}/>
          </div>
<!--          <div style="text-align:center">{item.name}</div>-->
        </li>
      {/each}
    </ul>
    <Button
      disabled={!validMsisdn || state.provider < 0}
      on:click={() => (showModal = true)}
      style="background:green;"
    >
      Continue
    </Button>

    {#if state.provider > -1}
      <Modal bind:open={showModal}>
        <div style="padding:1rem">
          <h1>Top up amount</h1>
          <div>
            Make sure the number is correct and select the amount you want to
            top up
          </div>
          <ul class="amount-options" on:click={onSelect(stateName.AMOUNT)}>
            {#each products[state.provider].options as item, i}
              <li
                class="half"
                data-index={i}
                class:selected={state.amount === i}
              >
                <div class="option amount">{`RM${item.amount.toFixed(2)}`}</div>
              </li>
            {/each}
          </ul>
          <Button disabled={!validMsisdn} on:click={onConfirm}>
            View Details
          </Button>
<!--          <Button disabled={!validMsisdn} on:click={onConfirm}>-->
<!--            Top up Now-->
<!--          </Button>-->
        </div>
      </Modal>
    {/if}

    {#if state.provider > -1}
      <Modal bind:open={showApplyVoucherModal}>
        <div style="padding:1rem">
          <h1>Order Details</h1>
          <div>
            Please confirm the phone number and the top up amount before proceeding.
          </div>
          <ul class="payment-info">
            <li class="payment-info__list">
              <div>Top-up Amount</div>
              <div>RM{products[state.provider].options[state.amount].amount.toFixed(2)}</div>
            </li>
            <li class="payment-info__list">
              <div>Discount Amount</div>
              <div style="color: red;">- RM{discountAmount.toFixed(2)}</div>
            </li>
            <li class="payment-info__list payable">
              <div>Payable Amount</div>
              <div>RM{payableAmount}</div>
            </li>
            {#if discountAmount > 0}
              <li>
                <div>
                  <span>Applied: {voucherCodeInput}</span>
                  <a on:click={cancelVoucher}>(Cancel)</a>
                </div>
              </li>
              {:else}
              <li class="payment-info__list">
                <div class="voucher-input">
                  <div class="input__wrapper">
                    <input
                      placeholder="Please enter voucher code"
                      value={voucherCodeInput}
                      autocomplete="off"
                      class:error={voucherCodeError !== ""}
                      type="text"
                      on:input={(e) => voucherCodeInput = e.target.value}
                    />
                    <span class="input__underline" class:error={voucherCodeError !== ""}/>
                  </div>
                  <div class="error">{voucherCodeError}</div>
                </div>
                <div style="width: 25%">
                  <Button outline on:click={onApplyVoucher}>
                    Apply
                  </Button>
                </div>
              </li>
              {/if}
            <li>
              <Button on:click={pay}>
                Pay RM{payableAmount}
              </Button>
            </li>
          </ul>
        </div>
      </Modal>
    {/if}
  </div>
{/if}

{#if loading}
  <div class="loading">
    <div class="loading-container">
      <div class="loading-element">
        <div class="lds-ring">
          <div/>
          <div/>
          <div/>
          <div/>
        </div>
      </div>
    </div>
  </div>
{/if}

<style lang="scss">
  :global(*) {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }

  :global(html, body) {
    width: 100%;
    height: 100%;
  }

  :global(body) {
    font-family: "Inter", sans-serif;
    font-size: 14px;
    background: #fff;
    padding: 0;
  }

  :global(i) {
    width: 60px;
    height: 60px;
    margin-bottom: 15px;
  }

  :global(.flag) {
    display: inline;
    width: 24px;
    height: 24px;
  }

  .header {
    display: flex;

    .icon {
      cursor: pointer;
      position: relative;
      display: flex;
      flex-direction: row;
      align-items: flex-start;
      padding: 4px;
      background: #f2f2f2;
      width: 24px;
      height: 24px;
      border-radius: 9999px;

      svg {
        width: 18px;
        height: 18px;
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
      }
    }
  }

  h1 {
    // flex: 1;
    font-size: 24px;
    line-height: 1;
    font-weight: 600;
    margin: 0 0 1rem;
  }

  .subtitle {
    color: #828282;
    margin: 5px 0;
  }

  .loading {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    transition: all 0.5s;
    background: rgba(0, 0, 0, 0.8);
    z-index: 10000;

    &-container {
      position: relative;
      height: 100%;
      width: 100%;
    }

    &-element {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);

      .lds-ring {
        display: inline-block;
        position: relative;
        width: 80px;
        height: 80px;
      }

      .lds-ring div {
        box-sizing: border-box;
        display: block;
        position: absolute;
        width: 64px;
        height: 64px;
        margin: 8px;
        border: 8px solid #fff;
        border-radius: 50%;
        animation: lds-ring 1.2s cubic-bezier(0.5, 0, 0.5, 1) infinite;
        border-color: #fff transparent transparent transparent;
      }

      .lds-ring div:nth-child(1) {
        animation-delay: -0.45s;
      }

      .lds-ring div:nth-child(2) {
        animation-delay: -0.3s;
      }

      .lds-ring div:nth-child(3) {
        animation-delay: -0.15s;
      }

      @keyframes lds-ring {
        0% {
          transform: rotate(0deg);
        }
        100% {
          transform: rotate(360deg);
        }
      }
    }
  }

  .error {
    color: red;
  }

  ul:not(.history-content__container) {
    padding: 1rem 0;
    list-style: none inside none;
    display: flex;
    flex-wrap: wrap;
    margin-left: -5px;
    margin-right: -5px;

    li {
      flex: 0 0 calc(100% / 3);

      .option {
        border: 1px solid #f6f6f6;
        margin: 5px;
        border-radius: 5px;
        padding: 5px;
        overflow: hidden;
        // height: 80px;
        position: relative;
      }

      img {
        display: block;
        margin: auto;
        // height: 100%;
        object-fit: cover;
      }

      .product-image {
        width: 100%;
      }

      .cashback-image {
        position: absolute;
        right: 0;
        bottom: top;
        width: 50%;
      }

      .amount {
        font-weight: 500;
        padding: 1rem 0;
        // font-size: 28px;
        align-self: center;
        justify-content: center;
        align-items: center;
        border-radius: 6px;
        text-align: center;
      }

      &.selected > .option {
        background: #f6f6f6;
        border-color: #000;
      }
    }

    li.half {
      flex: 0 0 50%;

      .option {
        margin: 5px;
      }
    }
  }

  /* UI */

  // .modal {
  //   position: fixed;
  //   bottom: 0;
  //   left: 0;
  //   right: 0;
  //   padding: 20px;

  //   &::before {
  //     background: rgba(0, 0, 0, 0.5);
  //     position: fixed;
  //     top: 0;
  //     bottom: 0;
  //     left: 0;
  //     right: 0;
  //   }
  // }

  .padding {
    padding: 20px;
  }

  .input {
    &__prefix {
      margin: 0 5px 0 10px;
    }

    &__wrapper {
      display: flex;
      align-items: center;
      border-bottom: 2px solid #ccc;
      margin-bottom: 5px;
      padding: 0.5rem;
      font-size: 18px;
      position: relative;

      input {
        flex: 1;
        display: block;
        outline: none;
        width: 100%;
        appearance: none;
        -webkit-appearance: none;
        -moz-appearance: none;
        border: none;
        transition: all 0.5s;
        font-size: 18px;

        &:focus + .input__underline {
          transform: scale(1);
        }

        &.error {
          color: inherit;
          border-color: red;
        }
      }

      .input__underline {
        background-color: #000;
        height: 2px;
        left: -2px;
        bottom: -2px;
        position: absolute;
        -webkit-transform: scale(0, 1);
        transform: scale(0, 1);
        -webkit-transition: all 0.15s linear;
        transition: all 0.15s linear;
        width: 100%;

        &.error {
          background-color: red;
          transform: scale(1);
        }
      }

      .input__icon {
        background-color: #333333;
        padding: 0.125rem;
        border-radius: 5px;

        svg {
          width: 18px;
          height: 18px;
          color: white;
        }
      }
    }
  }

  ul.payment-info {
    display: block;

    li.payment-info__list {
      width: 100%;
      display: flex;
      justify-content: space-between;
      margin-bottom: 5px;
      align-items: center;

      &.payable {
        font-weight: 600;
        font-size: 16px;
      }

      & > .voucher-input {
        flex: 1;
        margin-right: 15px;

        & input {
          font-size: 14px;
        }
      }
    }
  }
</style>
